<script setup lang="ts">
import TsModal from './ts-modal.vue'
import Alert from './alert.vue'
import LoadingIndicator from './loading-indicator.vue'

import {computed, ref} from "vue";
import type {CancellationReason} from "@/js/vue-components/types";
import {isNonEmptyString} from "@/js/js-utils";
import axios from "axios";
import Rails from "@rails/ujs";

export interface Props {
  cancelUrl: string
  registrationUrl: string
  cancellationReasons?: CancellationReason[]
}

const props = withDefaults(defineProps<Props>(), {
  cancellationReasons: () => [],
})

const errorMessage = ref<string | null>(null)
const errorDetails = ref<string | null>(null) // remove?
const isLoading = ref<boolean>(false)
const cancellationReason = ref<string>('')
const errorType = ref<string>('')
const selectedReason = ref<CancellationReason | null>(null)

const showError = computed<boolean>(() => isNonEmptyString(errorMessage.value))
const showForm = computed<boolean>(() => !isLoading.value)
const requireCancellationComment = computed<boolean>(() => selectedReason.value?.requireComment || false)

const modal = ref<InstanceType<typeof TsModal> | null>(null)

const clearError = (): void => {
  isLoading.value = false
  errorType.value = 'danger'
  errorMessage.value = null
  errorDetails.value = null
}

const setErrorMessage = (anErrorMessage: string): void => {
  errorMessage.value = anErrorMessage
}

const showModal = (): void => {
  clearError()
  cancellationReason.value = ''
  modal.value?.show()
}

const hideModal = (): void => {
  modal.value?.hide()
}

const cancelRegistration = (): void => {
  if (isLoading.value) {
    return
  }

  isLoading.value = true

  const formData = new FormData()
  formData.append('reason', cancellationReason.value)
  if (selectedReason.value) {
    formData.append('reason_id', selectedReason.value.id)
  }
  formData.append('_method', 'DELETE')

  axios.post(props.cancelUrl, formData, {
    headers: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'X-Requested-With': 'XMLHttpRequest',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'X-CSRF-Token': Rails.csrfToken()
    }
  })
    .then((response) => {
      if (typeof response.data.message === 'string') {
        if (response.data.success) {
          if (isNonEmptyString(props.registrationUrl)) {
            window.location.href = props.registrationUrl
          }
        } else {
          errorMessage.value = response.data.message
          isLoading.value = false
        }
      } else {
        errorMessage.value = 'Unknown response from server'
        isLoading.value = false
      }
    })
    .catch((error) => {
      errorMessage.value = error.message
      isLoading.value = false
    })
}

defineExpose({setErrorMessage, showModal, hideModal, showError})
</script>

<template>
  <TsModal
    ref="modal"
    modal-title="Cancel participation"
    modal-class="modal-form cancel-registration-modal"
  >
    <template #modal-content>
      <template v-if="showForm">
        <Alert
          :type="errorType"
          :message="errorMessage"
          :details-html="errorDetails"
          @dismiss="clearError"
        />
        <Alert
          type="danger"
          :dismissable="false"
          message="You are about to cancel your participation, are you sure?"
        />
        <div
          v-if="cancellationReasons.length"
          class="mb-2"
        >
          <label class="form-label required">Cancellation reason</label>
          <div
            v-for="reason of cancellationReasons"
            :key="reason.id"
          >
            <div class="form-check">
              <input
                :id="`reason-${reason.id}`"
                v-model="selectedReason"
                :value="reason"
                type="radio"
                name="reason_id"
                class="form-check-input"
              >
              <label
                class="form-check-label"
                :for="`reason-${reason.id}`"
              >
                {{ reason.title }}
              </label>
            </div>
          </div>
        </div>
        <div class="mb-2">
          <label
            for="cancellation-reason"
            class="form-label"
            :class="{required: requireCancellationComment }"
          >
            Comment
          </label>
          <textarea
            id="cancellation-reason"
            v-model="cancellationReason"
            class="form-control"
          />
        </div>
      </template>
      <LoadingIndicator
        v-if="isLoading"
        :vertical="true"
        message="Please wait while action is performed ..."
      />
    </template>
    <template #modal-footer>
      <button
        type="button"
        class="btn btn-sm btn-success"
        :disabled="isLoading"
        data-bs-dismiss="modal"
      >
        No
      </button>
      <button
        type="button"
        :disabled="isLoading"
        class="btn btn-sm btn-danger"
        @click.prevent="cancelRegistration"
      >
        Yes
      </button>
    </template>
  </TsModal>
</template>
